import { Box, Button, Chip, Typography } from '@mui/material';
import { fMonthDayTimeYear } from '../../utils/formatTime';
import Iconify from '../iconify';
import UrgentToggle from './UrgentToggle';
import TicketsLeftLabel from '../../components/dashboard/tickets/TicketsLeftLabel';

const cellSx = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
}

const renderIcon = (value, icon) => (
  <Box>
    <Iconify icon={icon} width={13} height={13} /> {value}
  </Box>
);
const renderChip = (value) =>
  value !== 'undefined' ? (
    <Chip
      color={value === 'Low' ? 'warning' : value === 'Medium' ? 'info' : value === 'High' ? 'secondary' : 'error'}
      label={value}
      size="small"
      sx={{
        color: '#fff',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        '&:hover': {
          cursor: 'pointer',
        },
      }}
    />
  ) : (
    <> </>
  );

const renderLeftLabelDashboard = (label) =>
  label === 'responded' ? (
    <Button
      size="small"
      variant="contained"
      sx={{
        ml: -2,
        mr: 1,
        mb: '2px',
        maxWidth: '10px',
        minWidth: '10px',
        width: '10px',
        height: '50px',
        borderRadius: 0,
        backgroundColor: '#fff',
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: '#fff',
          opacity: 0.8,
        },
      }}
    />
  ) : (
    <Button
      size="small"
      variant="contained"
      sx={{
        ml: -2,
        mr: 1,
        mb: '2px',
        maxWidth: '10px',
        minWidth: '10px',
        width: '10px',
        height: '50px',
        borderRadius: 0,
        backgroundColor: '#208ee0',
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: '#208ee0',
          opacity: 0.8,
        },
      }}
    />
  );

  const dateAdded = {
    field: 'createdOn',
    headerName: 'Date Added',
    flex: 1,
    minWidth: 85,
    renderCell: (params) => (
      <Box component="div" sx={cellSx}>
        {renderIcon(`${fMonthDayTimeYear(params.value)} EST`, 'material-symbols:calendar-today-outline')}
      </Box>
    ),
    valueGetter: (params) => new Date(params.value),
    type: 'customDate',
  };

  const lastResponseOn = {
    field: 'lastSentOn',
    headerName: 'Last Response On',
    flex: 1,
    minWidth: 85,
    disableReorder: true,
    renderCell: (params) => (
      <Box component="div" sx={cellSx}>
        {params.value
          ? renderIcon(`${fMonthDayTimeYear(params.value)} EST`, 'material-symbols:calendar-today-outline')
          : ''}
      </Box>
    ),
    valueGetter: (params) => (params.value ? new Date(params.value) : ''),
    type: 'customDate',
  };

const createdBy = {
  field: 'createdBy',
  minWidth: 120,
  headerName: 'Created By',
  renderCell: (params) => (
    <Box component="div" sx={cellSx}>
      {params.value}
    </Box>
  ),
  flex: 2,
  type: 'customSelect',
};

const lastResponseBy = {
  field: 'lastSentBy',
  minWidth: 120,
  headerName: 'Last Response By',
  renderCell: (params) => (
    <Box component="div" sx={cellSx}>
      {params.value}
    </Box>
  ),
  flex: 2,
  type: 'customSelect',
};
const facility = {
  field: 'facility',
  headerName: 'Facility',
  flex: 1,
  renderCell: (params) => (
    <Box component="div" sx={cellSx}>
      {params.value}
    </Box>
  ),
  minWidth: 80,
  type: 'customSelect',
};
const company = {
  field: 'company',
  headerName: 'Group',
  minWidth: 80,
  type: 'customSelect',
  flex: 1,
  renderCell: (params) => (
    <Box component="div" sx={cellSx}>
      {params.value}
    </Box>
  ),
};
const extraInfo = {
  field: 'extraInfo',
  headerName: 'Subject',
  flex: 1.5,
  minWidth: 110,
  type: 'customSelect',
  renderCell: (params) => (
    <Box component="div" sx={cellSx}>
      <Typography variant="subtitle2" style={{ display: 'inline' }}>
        {params.row.extraInfo}
      </Typography>

      {params.row.lastMessage &&
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {" "}-{" "}
          {params.row?.lastMessage?.split(' ')?.slice(0, 10)?.join(' ')}
        </Typography>
      }
    </Box>
  ),
};

const category = {
  field: 'category',
  headerName: 'Category',  
  minWidth: 80,
  type: 'customSelect',
};

const ticketType = {
  field: 'ticketType',
  headerName: 'Type',
  minWidth: 80,
  flex: 1,
  type: 'customSelect',
};

const extraInfoDashboard = {
  field: 'extraInfo',
  headerName: 'Subject',
  flex: 1.5,
  type: 'customSelect',
  renderCell: (params) => (
    <Box component="div" sx={cellSx}>
      {params.row.extraInfo}
    </Box>
  ),
};

const orderNumber = {
  field: 'ticketOrderId',
  headerName: 'Order #',
  flex: 1,
  minWidth: 80,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      sx={{ cursor: "pointer", ...cellSx }}
      onClick={(event) => {
        if (params.value > 0) {
          event.navigateToOrders = params.value;
        }
      }}
    >
      <Typography
        variant="subtitle2"
        // color="secondary"
        component="span" // Use span here to prevent nested paragraphs
        sx={{
          textDecoration: 'underline',
        }}
      >
        {params.value ? params.value : ''}
      </Typography>
      {params.value > 0 && (
        <Typography component="span" sx={{ color: 'secondary.main' }}>
          <Iconify icon="material-symbols:open-in-new-sharp" width={16} height={16} />
        </Typography>
      )}
    </Box>
  ),
};
const ticketNumber = { field: 'id', headerName: 'Ticket #', flex: 0.75, minWidth: 80, type: 'customSelect' };

const importance = {
  field: 'importance',
  headerName: 'Priority Level',
  flex: 0.95,
  minWidth: 80,
  renderCell: (params) => params.value && renderChip(params.value),
  valueGetter: (params) => params.row.importance,
  type: 'customSelect',
};

const importanceDashboard = {
  field: 'importance',
  headerName: 'Priority Level',
  flex: 1,
  minWidth: 80,
  renderCell: (params) => params.value && renderChip(params.value),
  valueGetter: (params) => params.row.importance,
  type: 'customSelect',
};

const labelUrgent = {
  field: 'respondedTo',
  headerName: '',
  width: 1,
  alignItems: 'left',
  renderCell: (params) => <UrgentToggle value={params.value} dashboard />,
};

const label = {
  field: 'label',
  headerName: '',
  minWidth: 15,
  resizable: false,
  maxWidth: 15,
  headerAlign: 'center',
  cellClassName: () => 'no-x-padding',
  headerClassName: () => 'no-x-padding no-resizable',
  renderCell: (params) => <TicketsLeftLabel ticket={params.row} />,
  valueGetter: (params) => (params.row.messageCount > 0 ? 'unresponded' : 'responded'),
  // filterable: false,
};

const labelDashboard = {
  field: 'label',
  headerName: '',
  minWidth: 30,
  maxWidth: 30,
  renderCell: (params) => renderLeftLabelDashboard(params.row.messageCount > 0 ? 'unresponded' : 'responded'),
  filterable: false,
};

const ACTIVE_TICKETS = [
  label,
  extraInfo,
  category,
  ticketType,
  importance,
  ticketNumber,
  dateAdded,
  facility,
  company,
  orderNumber,
  createdBy,
  lastResponseBy,
  lastResponseOn,
];

const DASHBOARD_TICKETS = [
  labelDashboard,
  labelUrgent,
  importanceDashboard,
  extraInfoDashboard,
  facility,
  // senderManagerDashboard
];
const COMPLETED_TICKETS = [extraInfo, importance, category, ticketNumber, dateAdded, createdBy, facility, company, orderNumber];
export { ACTIVE_TICKETS, COMPLETED_TICKETS, DASHBOARD_TICKETS };
